// apps/client/src/ui/screens/roof.tsx (M1-T14) — the cottage roof picker, opened from the loan screen at the tent → cottage step:
// exactly the four KayKit roof colours of `look.roofs` (Contract change 35), each a swatch button named by its string, with a still of
// the cottage in the picked colour when 3D is available. Build it sends `house.upgrade { roof }` and closes back to the loan screen.
import { friendColors, look, tuning, type RoofColor, type StringKey } from '@whiskerton/content';
import { useEffect, useState, type CSSProperties } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { webglIconRenderer } from '../../assets/icons';
import type { AssetService } from '../../assets/service';
import { getGame, useCommands } from '../../game/instance';
import { requestUpgrade } from '../../state/slices/shop';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';
import { injectShopStyle } from './sell';
import { hourText } from './shop';

declare module '../router' { interface ScreenParams { roof: undefined } }

const PREVIEW_PX = 256;
const stills = new Map<string, Promise<string>>();
/** A still of the level-1 cottage in `roof` (with your garden), rendered once per colour by the shared offscreen icon renderer, so the
 *  picker opens no WebGL context of its own. Null without WebGL or kits: the swatches alone then. */
function useCottageStill(roof: RoofColor, friendDye: string): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const assets = getGame().assets as Partial<AssetService> | undefined;
    if (!assets?.model) return;
    const key = `${roof}:${friendDye}`;
    let p = stills.get(key);
    if (!p) {
      p = assets.model('w.house', { level: 1, roof, friendDye, building: false, resident: true }).then((o) => webglIconRenderer().render(o, PREVIEW_PX));
      p.catch(() => stills.delete(key));
      stills.set(key, p);
    }
    p.then((url) => { if (live) setSrc(url); }, () => { if (live) setSrc(null); });
    return () => { live = false; };
  }, [roof, friendDye]);
  return src;
}

function Roof({ close }: { close: () => void }) {
  injectShopStyle();
  const t = useT();
  const cmd = useCommands();
  const { color, toast } = useGame(useShallow((s) => ({ color: s.you ? s.residents[s.you.resident]?.color : undefined, toast: s.toast })));
  const [pick, setPick] = useState<RoofColor>(look.roofs[0]!.id);
  const [busy, setBusy] = useState(false);
  const still = useCottageStill(pick, color ? friendColors[color].dye : '');
  const confirm = () => {
    setBusy(true);
    void requestUpgrade(cmd.act, pick).then((r) => {
      setBusy(false);
      if (!r.ok) return;
      toast('shop.upgraded', { hour: hourText(tuning.time.constructionHour) });   // Bramble's line (Contract change 31)
      close();
    });
  };
  return (
    <Panel title={t('shop.roofTitle')} className="shop-panel">
      {/* Side by side: on a phone the stacked preview pushed the swatches below the fold (M1 playtest, roof shots 23-25). */}
      <div className={`roof-layout${still ? '' : ' no-preview'}`}>
        {still && <img className="roof-preview" src={still} alt="" />}
        <div className="roof-side">
          <div className="swatch-row" role="group" aria-label={t('shop.roofTitle')}>
            {look.roofs.map((r) => (
              <button key={r.id} type="button" className="swatch" aria-pressed={r.id === pick} aria-label={t(r.name as StringKey)}
                style={{ '--swatch': r.hex } as CSSProperties} onClick={() => setPick(r.id)} />
            ))}
          </div>
          <p className="shop-note">{t('shop.roofNote')}</p>
          <Button disabled={busy} onClick={confirm}>{t('shop.roofConfirm')}</Button>
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'roof'> = { id: 'roof', layer: 'panel', component: ({ close }) => <Roof close={close} /> };
